/**
 * Minimaler Client-Router fuer die App-Shell (SPA).
 *
 * Aufgaben (SRP):
 *  - URL -> Route-Modul auflösen und die View ins Shell-Outlet montieren
 *  - <a data-nav>-Klicks und popstate abfangen — kein Full-Page-Reload
 *  - Lifecycle: View bekommt mount(ctx) und einen teardown-Hook, den der
 *    Router beim Wechsel aufruft (Timer/Polling/Listener sauber abbauen)
 *
 * Bewusst KEIN Framework: Vanilla ES-Module + dynamic import, wie im Rest
 * des Clients.
 */
import { skeleton } from './dom.js';

export interface RouteContext {
  params: URLSearchParams;
  session: import('./api.js').SessionInfo | null;
}

export interface View {
  /** Baut die View ins Outlet und liefert einen optionalen teardown. */
  mount(outlet: HTMLElement, ctx: RouteContext): void | Promise<void>;
}

/**
 * Ein View-Modul liefert die View als default-Export und darf zusaetzlich ein
 * `teardown` exportieren, das der Router beim Verlassen der Route aufruft
 * (Timer, Polling, Listener abraeumen).
 */
type ViewFactory = () => Promise<{ default: View; teardown?: () => void }>;

export interface RouteDef {
  path: string; // exakter Pfad, z. B. '/app/analysen'; ':id' als Segment-Param
  load: ViewFactory;
  /** Wenn true, wird die View-Chunk gar nicht geladen solange kein Login.
   *  Stattdessen rendert der Router eine schlanke Guard-Card direkt — schont
   *  Bandbreite/JS-Bundle, weil der User den Chunk sowieso nicht zu sehen
   *  bekommt. (Saso: 'macht wenig sinn, das fuer anon zu laden.') */
  requiresAuth?: boolean;
}

export interface RouterHooks {
  /** Vor jedem Wechsel — liefert false, um den Wechsel zu verhindern. */
  before?: (to: RouteDef | null) => boolean | Promise<boolean>;
}

export class Router {
  private routes: RouteDef[] = [];
  private teardown: (() => void) | null = null;
  private outlet: HTMLElement | null = null;
  private hooks: RouterHooks = {};
  private currentPath: string | null = null;
  private loadToken = 0; // gegen Race: schneller alter Import nach neuem Navigate

  constructor(outlet: HTMLElement, hooks: RouterHooks = {}) {
    this.outlet = outlet;
    this.hooks = hooks;
    document.addEventListener('click', (event) => this.onClick(event));
    window.addEventListener('popstate', () => {
      void this.render(location.pathname + location.search, false);
    });
  }

  /** Registriert eine Route (Pfad mit optionalen :param-Segmenten). */
  add(path: string, load: ViewFactory, opts: { requiresAuth?: boolean } = {}): this {
    this.routes.push({ path, load, requiresAuth: opts.requiresAuth ?? false });
    this.routes.sort((a, b) => b.path.length - a.path.length);
    return this;
  }

  /** Startet das Routing fuer die aktuelle URL. */
  start(): void {
    void this.render(location.pathname + location.search, false);
  }

  /** Programmatischer Wechsel (Form-Redirects etc.). */
  navigate(to: string, replace = false): void {
    if (replace) history.replaceState(null, '', to);
    else history.pushState(null, '', to);
    void this.render(to, false);
  }

  private onClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('/app')) return;
    if (!this.match(href.split('?')[0] ?? href)) return; // unbekannte Route: Browser
    event.preventDefault();
    if (href === this.currentPath + location.search) return;
    this.navigate(href);
  }

  private match(pathname: string): RouteDef | null {
    for (const route of this.routes) {
      const params = matchPath(route.path, pathname);
      if (params) return { ...route, params } as RouteDef;
    }
    return null;
  }

  private async render(full: string, _fromPop: boolean): Promise<void> {
    const [pathname, query = ''] = full.split('?');
    const route = this.match(pathname ?? full);
    if (!route) {
      location.href = full; // Fallback: Server rendert (Landing etc.)
      return;
    }
    if (this.hooks.before && !(await this.hooks.before(route))) return;

    const token = ++this.loadToken;
    this.teardown?.();
    this.teardown = null;
    this.currentPath = pathname ?? full;

    // Anonym + Auth-Pflicht: direkt zur Login-Maske umleiten, statt eine
    // Guard-Card anzuzeigen — der User soll nicht erst eine
    // "Bitte anmelden"-Notice lesen und dann selbst klicken muessen
    // (Saso 09.09.2026: 'wenn man nicht angemeldet ist, dann kommt ein
    // redirect page - unnoetig. soll gleich zur login maske'). currentPath
    // aendern wir auf die Login-Route, damit spaetere Render-Aufrufe nach
    // erfolgreichem Login (refreshAfterLogin -> router.navigate) nicht die
    // alte Anonyme-URL erneut treffen.
    if (route.requiresAuth && !this.session?.authenticated) {
      const next = encodeURIComponent(location.pathname + location.search);
      const loginUrl = `/app/login?next=${next}`;
      // replaceState statt pushState: "Zurueck" nach erfolgreichem Login
      // fuehrt auf die vorherige Aussenwelt-Seite, nicht zurueck auf die
      // gerade abgewiesene Auth-Route (die wuerde sofort wieder
      // umleiten und der User landet in einer Endlosschleife).
      history.replaceState(null, '', loginUrl);
      this.currentPath = '/app/login';
      if (this.outlet) this.outlet.replaceChildren();
      this.teardown = () => {
        /* keine Listener, nichts zu raeumen */
      };
      // Login-Route ist nicht requiresAuth -> wird im naechsten Loop
      // ganz normal gerendert (kein 2. render()-Aufruf noetig, weil
      // currentPath gesetzt ist und der Caller ohnehin den gleichen
      // render()-Aufruf nochmal anstoesst; aber hier ist es sicherer,
      // den Routen-Wechsel selbst auszuloesen).
      void this.render(loginUrl, false);
      return;
    }

    try {
      // Route-Skeleton: der Chunk-Import dauert beim ERSTEN Besuch einer
      // Sektion spuerbar lang — vorher blieb die alte Seite einfach stehen,
      // ohne jedes Feedback (Saso 07.09.2026: 'wenn ich die sektion wechsel,
      // kommt kein spinner?'). Delay 120 ms: bei gecachten Chunks ist der
      // Wechsel schneller als das Delay und nichts flackert — genau wie
      // vorher. Erst darueber zeigt das Outlet ein Ladegeruest, bis die
      // View ihr eigenes Skeleton montiert.
      let skeletonTimer: ReturnType<typeof setTimeout> | null = null;
      if (this.outlet) {
        const outletRef = this.outlet;
        const tok = token;
        skeletonTimer = setTimeout(() => {
          if (tok === this.loadToken && outletRef.isConnected) {
            outletRef.replaceChildren(skeleton('title', 'line', 'line'));
          }
        }, 120);
      }
      try {
        const mod = await route.load();
        // Skeleton-Timer SOFORT nach dem Import stoppen. mount() baut sein
        // eigenes Ladegeruest synchron — aber seine API-Awaits halten das
        // render-Promise Sekunden offen. feuert der Timer erst im finally,
        // schlaegt er IN der Async-Phase zu und ersetzt das fertig montierte
        // DOM durch ein Skeleton, das dann ewig stehen bleibt (Vorfall
        // 07.09.2026: '/app/account zeigt nur loading' — Panels montiert,
        // alle API-Calls 200, Skeleton gewann).
        if (skeletonTimer) { clearTimeout(skeletonTimer); skeletonTimer = null; }
        if (token !== this.loadToken) return; // zwischenzeitlich weitergenavigiert
        if (!this.outlet) return;
        this.outlet.replaceChildren();
        const view: View = mod.default;
        // Teardown des Moduls uebernehmen, BEVOR gemountet wird: bricht mount()
        // ab, sind angelegte Timer trotzdem abraeumbar.
        //
        // Das fehlte bis 31.08.2026: thread.ts exportierte teardown(), der Router
        // hat es nie abgeholt — der 1,5-s-Poll einer laufenden Analyse lief
        // weiter, nachdem der Nutzer die Seite verlassen hatte, und mit jedem
        // Besuch kam ein weiterer dazu.
        if (typeof mod.teardown === 'function') this.teardown = mod.teardown;
        await view.mount(this.outlet, {
          params: new URLSearchParams(query),
          session: this.session ?? null,
        });
      } finally {
        if (skeletonTimer) clearTimeout(skeletonTimer); // Sicherheitsnetz (Load-Fehler)
      }
    } catch (err) {
      console.error('[router] view failed', err);
      if (this.outlet) {
        this.outlet.replaceChildren(
          Object.assign(document.createElement('div'), {
            className: 'notice notice--error',
            textContent: 'Die Seite konnte nicht geladen werden.',
          }),
        );
      }
    }
  }

  /** Session-Ref, die main.ts nach /api/session setzt. */
  session: import('./api.js').SessionInfo | null = null;
  }

function matchPath(pattern: string, pathname: string): URLSearchParams | null {
  const pSegs = pattern.split('/').filter(Boolean);
  const uSegs = pathname.split('/').filter(Boolean);
  if (pSegs.length !== uSegs.length) return null;
  const params = new URLSearchParams();
  for (let i = 0; i < pSegs.length; i++) {
    const p = pSegs[i] ?? '';
    const u = uSegs[i] ?? '';
    if (p.startsWith(':')) params.set(p.slice(1), decodeURIComponent(u));
    else if (p !== u) return null;
  }
  return params;
}
